import { useState, useCallback } from 'react';
import L from 'leaflet';
import { Customer } from '../../../../api/mobiApi/domain/domain.types';


interface Point {
  x: number;
  y: number;
  lat: number;
  lng: number;
}

interface CompletedArea {
  id: string;
  points: Point[];
  type: 'polygon';
  customerIds: string[];
}

export const useMultiAreaSelection = (
  mapInstance: L.Map | null,
  customers: Customer[]
) => {
  const [isActive, setIsActive] = useState(false);
  const [completedAreas, setCompletedAreas] = useState<CompletedArea[]>([]);
  const [accumulatedCustomers, setAccumulatedCustomers] = useState<Customer[]>([]);

  // Current drawing state
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentPoints, setCurrentPoints] = useState<Point[]>([]);
  const [currentMousePoint, setCurrentMousePoint] = useState<Point | null>(null);

  // Start multi-area selection mode (polygon only)
  const startMultiSelection = useCallback(() => {
    setIsActive(true);
    setCompletedAreas([]);
    setAccumulatedCustomers([]);
    setIsDrawing(true);
    setCurrentPoints([]);
    setCurrentMousePoint(null);
  }, []);

  // Add point to current polygon
  const addPoint = useCallback((e: React.MouseEvent) => {
    if (!mapInstance || !isDrawing) return;

    const mapContainer = mapInstance.getContainer();
    const mapRect = mapContainer.getBoundingClientRect();

    const containerX = e.clientX - mapRect.left;
    const containerY = e.clientY - mapRect.top;
    const latLng = mapInstance.containerPointToLatLng([containerX, containerY]);

    const newPoint: Point = {
      x: e.clientX,
      y: e.clientY,
      lat: latLng.lat,
      lng: latLng.lng
    };

    setCurrentPoints(prev => [...prev, newPoint]);
    console.log('📍 Point added:', newPoint);
  }, [mapInstance, isDrawing]);

  // Update mouse position for preview
  const updateMousePosition = useCallback((e: React.MouseEvent) => {
    if (!mapInstance || !isDrawing) return;

    const mapContainer = mapInstance.getContainer();
    const mapRect = mapContainer.getBoundingClientRect();

    const containerX = e.clientX - mapRect.left;
    const containerY = e.clientY - mapRect.top;
    const latLng = mapInstance.containerPointToLatLng([containerX, containerY]);

    setCurrentMousePoint({
      x: e.clientX,
      y: e.clientY,
      lat: latLng.lat,
      lng: latLng.lng
    });
  }, [mapInstance, isDrawing]);

  // Finish current polygon
  const finishCurrentPolygon = useCallback(() => {
    if (!mapInstance || currentPoints.length < 3) {
      console.warn('⚠️ Polygon needs at least 3 points');
      return;
    }

    const polygonCoords = currentPoints.map(p => [p.lat, p.lng]);

    // Find customers within polygon
    const selectedInArea = customers.filter(customer => {
      const point: [number, number] = [customer.lat, customer.lon];
      return isPointInPolygon(point, polygonCoords);
    });

    // Add to accumulated customers (avoid duplicates)
    const existingIds = new Set(accumulatedCustomers.map(c => c.id));
    const newCustomers = selectedInArea.filter(c => !existingIds.has(c.id));

    if (newCustomers.length > 0) {
      const areaId = `area_${Date.now()}`;
      const newArea: CompletedArea = {
        id: areaId,
        points: [...currentPoints],
        type: 'polygon',
        customerIds: newCustomers.map(c => c.id)
      };

      setCompletedAreas(prev => [...prev, newArea]);
      setAccumulatedCustomers(prev => [...prev, ...newCustomers]);

      console.log(`✅ Polygon completed: ${newCustomers.length} new customers`);
    }

    // Reset for next drawing
    setCurrentPoints([]);
    setCurrentMousePoint(null);
    setIsDrawing(true); // Keep drawing mode active for next area
  }, [mapInstance, currentPoints, customers, accumulatedCustomers]);

  // Finish entire multi-selection (submit all)
  const finishMultiSelection = useCallback((onComplete?: (customers: Customer[]) => void) => {
    console.log(`📊 Multi-selection finished: ${accumulatedCustomers.length} total customers from ${completedAreas.length} areas`);

    if (onComplete && accumulatedCustomers.length > 0) {
      onComplete(accumulatedCustomers);
    }

    // Reset everything
    setIsActive(false);
    setIsDrawing(false);
    setCurrentPoints([]);
    setCurrentMousePoint(null);
    setCompletedAreas([]);
    setAccumulatedCustomers([]);
  }, [accumulatedCustomers, completedAreas]);

  // Cancel multi-selection
  const cancelMultiSelection = useCallback(() => {
    setIsActive(false);
    setIsDrawing(false);
    setCurrentPoints([]);
    setCurrentMousePoint(null);
    setCompletedAreas([]);
    setAccumulatedCustomers([]);
  }, []);

  // Remove last area
  const removeLastArea = useCallback(() => {
    setCompletedAreas(prev => {
      if (prev.length === 0) return prev;

      const lastArea = prev[prev.length - 1];
      const lastAreaCustomerIds = new Set(lastArea.customerIds);

      // Remove customers from last area
      setAccumulatedCustomers(current =>
        current.filter(c => !lastAreaCustomerIds.has(c.id))
      );

      console.log('↶ Last area removed');
      return prev.slice(0, -1);
    });
  }, []);

  return {
    isActive,
    isDrawing,
    currentPoints,
    currentMousePoint,
    completedAreas,
    accumulatedCustomers,
    startMultiSelection,
    addPoint,
    updateMousePosition,
    finishCurrentPolygon,
    finishMultiSelection,
    cancelMultiSelection,
    removeLastArea
  };
};

// Point in polygon algorithm
function isPointInPolygon(point: [number, number], polygon: number[][]): boolean {
  const [x, y] = point;
  let inside = false;

  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [xi, yi] = polygon[i];
    const [xj, yj] = polygon[j];

    const intersect = ((yi > y) !== (yj > y)) &&
      (x < (xj - xi) * (y - yi) / (yj - yi) + xi);

    if (intersect) inside = !inside;
  }

  return inside;
}
